import { CancelConfirmModal, LoadingPage } from '@chiroup/components';
import {
  AppointmentStatuses,
  ChiroUpAppointmentCommon,
  ChiroUpDayJsCommon,
  HealthComLocation,
} from '@chiroup/core';
import { TrashIcon } from '@heroicons/react/24/solid';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import useAppointments from '../../hooks/useAppointments';
import ChangeAppointmentButton from './ChangeAppointmentButton';

type Props = {
  location?: HealthComLocation;
  path: string;
  primaryColor: string;
  me: { patients: any[] } | null;
};

const Appointments: React.FC<Props> = ({
  location,
  path,
  primaryColor,
  me,
}) => {
  const { data, cancel, isCancelling, updateAppointment, isFetching } =
    useAppointments();
  const [cancelId, setCancelId] = useState<string | null>(null);

  const [error, setError] = useState({
    active: false,
    text: '',
    cancelText: 'Close',
  });

  if (!location) {
    return null;
  }

  return (
    <>
      <div className="mx-auto max-w-5xl flex flex-col gap-12 sm:gap-24">
        {isFetching ? (
          <LoadingPage />
        ) : data?.length ? (
          <div className="flex flex-col gap-4">
            <div className="md:flex md:items-center md:justify-between">
              <div className="min-w-0 flex-1">
                <h2 className="text-2xl font-bold text-gray-900 sm:truncate sm:text-3xl">
                  Manage Appointments
                </h2>
              </div>
            </div>
            <ul className="divide-y divide-gray-100 bg-white px-6 rounded-md shadow-sm border border-gray-300">
              {data?.map((appointment, idx) => {
                const provider = location?.providers?.find(
                  (prov) => prov.ID === appointment.clinicianId,
                );
                return (
                  <li
                    key={appointment.id || idx}
                    className="flex flex-col sm:flex-row items-left sm:items-center gap-y-4 sm:gap-y-0 sm:justify-between gap-x-6 py-5"
                  >
                    <div className="flex flex-col min-w-0">
                      <div className="flex items-start gap-x-3">
                        <p className="text-sm font-semibold leading-6 text-gray-900">
                          {provider?.name} (
                          {appointment?.displayValues?.treatmentName}){' '}
                          {me?.patients?.length !== 1
                            ? ` for ${appointment?.displayValues?.patientName}`
                            : ''}
                        </p>
                        {appointment.status ===
                          AppointmentStatuses.Canceled && (
                          <p className="rounded-md whitespace-nowrap mt-0.5 px-1.5 py-0.5 text-xs font-medium ring-1 ring-inset text-red-700 bg-red-50 ring-red-500/10">
                            Canceled
                          </p>
                        )}
                      </div>
                      <div className="mt-1 flex flex-col sm:flex-row items-left sm:items-center gap-x-2 text-xs leading-5 text-gray-500">
                        <p className="whitespace-nowrap">
                          Scheduled for{' '}
                          {ChiroUpDayJsCommon.cleanupDatetime(
                            appointment.startTimeFormatted,
                          )}
                        </p>
                        <svg
                          viewBox="0 0 2 2"
                          className="h-0.5 w-0.5 fill-current hidden sm:block"
                        >
                          <circle cx={1} cy={1} r={1} />
                        </svg>
                        <p className="truncate">
                          Approximately {appointment.duration} minutes
                        </p>
                      </div>
                    </div>
                    {appointment.status === AppointmentStatuses.Scheduled && (
                      <div className="flex flex-none items-center gap-x-4">
                        <ChangeAppointmentButton
                          primaryColor={primaryColor}
                          location={location}
                          id={appointment.id}
                          provider={appointment.clinicianId}
                          treatment={appointment.treatmentId}
                          startTime={appointment.startTime}
                          startTimeFormatted={appointment.startTimeFormatted}
                          cancel={() => {
                            setCancelId(appointment.id);
                          }}
                          updateAppointment={updateAppointment}
                          path={path}
                        />
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        ) : (
          <div className="px-4 py-16 sm:px-6 sm:py-24 md:grid md:place-items-center lg:px-8">
            <div className="max-w-max mx-auto">
              <main className="sm:flex">
                <div className="sm:ml-6">
                  <div className="sm:pl-6">
                    <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight sm:text-5xl">
                      No Appointments Found
                    </h1>
                    <p className="mt-1 text-base text-gray-500">
                      No appointments could be found that match the information
                      entered when signing in.
                    </p>
                  </div>
                  <div className="mt-10 flex space-x-3 sm:pl-6">
                    <Link
                      to={`/${path}`}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      style={{
                        backgroundColor: primaryColor,
                      }}
                    >
                      Create Appointment Now
                    </Link>
                  </div>
                </div>
              </main>
            </div>
          </div>
        )}
      </div>
      <CancelConfirmModal
        isOpen={!!cancelId}
        title="Cancel appointment"
        confirmText="Cancel Appointment"
        description="Are you sure you want to cancel this appointment? You can reschedule at any time."
        close={() => {
          setError({ ...error, active: false, text: '' });
          setCancelId(null);
        }}
        setCancelModalOpen={() => setCancelId(null)}
        selectedAppointmentId={cancelId}
        saveAppointmentFromHealthComSchedule={async (
          id: string,
          reason: string,
        ) => {
          setError({ ...error, active: false, text: '' });
          try {
            await cancel(id, reason);
            setCancelId(null);
          } catch (e: any) {
            setError({
              ...error,
              active: true,
              text:
                e.response?.data?.message ||
                ChiroUpAppointmentCommon.cancelFailed,
            });
          }
        }}
        error={error}
        primaryColor={primaryColor}
        noColor={!primaryColor}
        cancelText="Keep Appointment"
        icon={
          <TrashIcon className="h-6 w-6 text-gray-600 dark:text-darkGray-400" />
        }
        loading={isCancelling}
      />
    </>
  );
};

export default Appointments;
